const ASK_HTTP_ERROR_FALLBACKS: Record<string, string> = {
  question_too_long:
    'Your question is too long. Please shorten it and try again.',
  question_required: 'A question is required.',
  invalid_request: 'Something went wrong. Please try again.',
  request_too_large:
    'Your request exceeds the allowed size. Please reduce the content and try again.',
  rate_limit_exceeded: 'Too many requests. Please wait a moment and try again.',
  service_overloaded:
    'The service is temporarily busy. Please try again shortly.',
  access_context_unavailable:
    'We’re unable to verify your access right now. Please try again shortly.',
  ask_failed:
    'We’re unable to process your request right now. Please try again shortly.',
  not_found: 'This request could not be found.',
};

const ASK_HTTP_STATUS_FALLBACKS: Record<number, string> = {
  413: ASK_HTTP_ERROR_FALLBACKS.request_too_large,
  429: ASK_HTTP_ERROR_FALLBACKS.rate_limit_exceeded,
  503: ASK_HTTP_ERROR_FALLBACKS.service_overloaded,
  502: ASK_HTTP_ERROR_FALLBACKS.ask_failed,
  404: ASK_HTTP_ERROR_FALLBACKS.not_found,
};

export const ASK_HTTP_ERROR_DEFAULT_MESSAGE =
  'Something went wrong. Please try again.';

export type AskHttpErrorBody = {
  error?: unknown;
  message?: unknown;
};

export function parseRetryAfterSeconds(header: string | null): number | null {
  if (!header?.trim()) return null;

  const asSeconds = Number(header);
  if (Number.isFinite(asSeconds) && asSeconds > 0) {
    return Math.ceil(asSeconds);
  }

  const asDate = Date.parse(header);
  if (Number.isNaN(asDate)) return null;
  const remainingSeconds = Math.ceil((asDate - Date.now()) / 1000);
  return remainingSeconds > 0 ? remainingSeconds : null;
}

function withRetryAfter(message: string, retryAfterSeconds: number | null) {
  if (!retryAfterSeconds) return message;
  const unit = retryAfterSeconds === 1 ? 'second' : 'seconds';
  return `${message} Try again in ${retryAfterSeconds} ${unit}.`;
}

export function getAskHttpErrorMessage(
  status: number,
  body: AskHttpErrorBody | null,
  retryAfterHeader: string | null
): string {
  const errorCode =
    typeof body?.error === 'string' ? body.error.trim() : '';
  const apiMessage =
    typeof body?.message === 'string' ? body.message.trim() : '';

  const text =
    apiMessage ||
    ASK_HTTP_ERROR_FALLBACKS[errorCode] ||
    ASK_HTTP_STATUS_FALLBACKS[status] ||
    ASK_HTTP_ERROR_DEFAULT_MESSAGE;

  const shouldAttachRetryAfter =
    status === 429 ||
    status === 503 ||
    errorCode === 'rate_limit_exceeded' ||
    errorCode === 'service_overloaded';

  if (!shouldAttachRetryAfter) return text;
  return withRetryAfter(text, parseRetryAfterSeconds(retryAfterHeader));
}
